'use client';
import { getColorScheme } from '@/utils/getColorScheme';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import {
    calculateEstimate,
    getVisibleFields,
    normalizeCalculatorConfig,
    SAMPLE_CALCULATORS,
} from '@/utils/jobPricingCalculator';
import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { Button } from './atoms/Button';
import { AnimatePresence, motion } from 'framer-motion';
import { Fragment, useEffect, useMemo, useState } from 'react';

const read = (value, fallback = '') =>
    value && typeof value === 'object' && 'value' in value
        ? value.value
        : value ?? fallback;
const coerceBoolean = (value, fallback = false) => {
    const resolved = read(value, fallback);
    if (typeof resolved === 'string')
        return ['true', '1', 'yes', 'on'].includes(
            resolved.trim().toLowerCase()
        );
    return typeof resolved === 'boolean' ? resolved : Boolean(resolved);
};
const money = (value, currency) =>
    new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 0,
    }).format(value);

const animationVariants = (preset, duration) => {
    const transition = { duration: Math.max(0.2, Number(duration) || 0.5) };
    if (preset === 'slide')
        return {
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0, transition },
        };
    if (preset === 'zoom')
        return {
            initial: { opacity: 0, scale: 0.96 },
            animate: { opacity: 1, scale: 1, transition },
        };
    return {
        initial: { opacity: 0 },
        animate: { opacity: 1, transition },
    };
};

function AnimatedBlock({ enabled, preset, duration, className, children }) {
    if (!enabled || preset === 'none')
        return <div className={className}>{children}</div>;
    const variants = animationVariants(preset, duration);
    return (
        <motion.div
            className={className}
            initial={variants.initial}
            whileInView={variants.animate}
            viewport={{ once: true, amount: 0.2 }}
        >
            {children}
        </motion.div>
    );
}

function StyledSelect({ value, onChange, options }) {
    const selected = options.find((option) => option.value === value);
    return (
        <Listbox value={value ?? ''} onChange={onChange}>
            <div className="relative inline-block max-w-full">
                <Listbox.Button className="flex max-w-full items-center justify-between rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-left text-sm text-neutral-900 shadow-sm outline-none transition hover:border-primary-300 focus:border-primary-500 focus:ring-2 focus:ring-primary-100">
                    <span className={selected ? '' : 'text-neutral-500'}>
                        {selected?.label || 'Select an option'}
                    </span>
                    <ChevronUpDownIcon
                        className="ml-3 h-5 w-5 shrink-0 text-primary-600"
                        aria-hidden="true"
                    />
                </Listbox.Button>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Listbox.Options className="absolute z-30 mt-2 max-h-64 w-max min-w-full max-w-md overflow-auto rounded-xl border border-primary-100 bg-white p-1.5 shadow-xl focus:outline-none">
                        <Listbox.Option
                            value=""
                            className={({ active }) =>
                                `cursor-pointer rounded-lg px-3 py-2.5 text-sm transition ${
                                    active
                                        ? 'bg-primary-50 text-primary-900'
                                        : 'text-neutral-500'
                                }`
                            }
                        >
                            Select an option
                        </Listbox.Option>
                        {options.map((option) => (
                            <Listbox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active }) =>
                                    `flex cursor-pointer items-center justify-between rounded-lg px-3 py-2.5 text-sm transition ${
                                        active
                                            ? 'bg-primary-50 text-primary-900'
                                            : 'text-neutral-800'
                                    }`
                                }
                            >
                                {({ selected: isSelected }) => (
                                    <>
                                        <span
                                            className={
                                                isSelected
                                                    ? 'font-semibold'
                                                    : 'font-medium'
                                            }
                                        >
                                            {option.label}
                                        </span>
                                        {isSelected && (
                                            <CheckIcon
                                                className="h-5 w-5 text-primary-600"
                                                aria-hidden="true"
                                            />
                                        )}
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );
}

function Field({ field, value, onChange, textColor = 'text-neutral-800' }) {
    const options = field.options || [];
    const numericCharacters = Math.max(String(value ?? '').length, 1);
    if (
        [
            'quantity',
            'number',
            'currency',
            'square-footage',
            'linear-footage',
            'distance',
            'rooms',
            'hours',
        ].includes(field.type)
    )
        return (
            <input
                aria-label={field.label}
                type="number"
                min={field.min ?? 0}
                max={field.max}
                step={field.step ?? 1}
                value={value ?? ''}
                onChange={(event) =>
                    onChange(
                        event.target.value === ''
                            ? ''
                            : Number(event.target.value)
                    )
                }
                style={{
                    boxSizing: 'content-box',
                    width: `${numericCharacters}ch`,
                }}
                className="max-w-full appearance-none rounded-xl border border-neutral-200 bg-white px-3 py-2.5 text-sm text-neutral-900 outline-none [appearance:textfield] focus:border-primary-500 focus:ring-2 focus:ring-primary-100 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
        );
    if (field.type === 'boolean')
        return (
            <label className={`flex items-center gap-3 text-sm ${textColor}`}>
                <input
                    type="checkbox"
                    checked={Boolean(value)}
                    onChange={(event) => onChange(event.target.checked)}
                    className="h-4 w-4 rounded border-neutral-300 text-primary-600"
                />{' '}
                Yes
            </label>
        );
    if (field.type === 'select' || field.type === 'smart-range')
        return (
            <StyledSelect value={value} onChange={onChange} options={options} />
        );
    if (field.type === 'checkbox')
        return (
            <div className="grid w-fit max-w-full gap-2 sm:grid-cols-2">
                {options.map((option) => {
                    const selected =
                        Array.isArray(value) && value.includes(option.value);
                    return (
                        <label
                            key={option.value}
                            className={`flex items-center gap-2 text-sm ${textColor}`}
                        >
                            <input
                                type="checkbox"
                                checked={selected}
                                onChange={() =>
                                    onChange(
                                        selected
                                            ? value.filter(
                                                  (item) =>
                                                      item !== option.value
                                              )
                                            : [...(value || []), option.value]
                                    )
                                }
                                className="h-4 w-4 rounded border-neutral-300 text-primary-600"
                            />
                            {option.label}
                        </label>
                    );
                })}
            </div>
        );
    return (
        <div className="grid w-fit max-w-full gap-2 sm:grid-cols-2">
            {options.map((option) => (
                <label
                    key={option.value}
                    className={`flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2 text-sm ${textColor}`}
                >
                    <input
                        type="radio"
                        name={field.id}
                        checked={value === option.value}
                        onChange={() => onChange(option.value)}
                        className="h-4 w-4 text-primary-600"
                    />
                    {option.label}
                </label>
            ))}
        </div>
    );
}

const formatAnswerForLeadForm = (field, answer) => {
    if (answer === undefined || answer === null || answer === '') {
        return 'Not provided';
    }
    if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';

    const options = field.options || [];
    const labelFor = (value) =>
        options.find((option) => option.value === value)?.label ??
        String(value);

    return Array.isArray(answer)
        ? answer.map(labelFor).join(', ')
        : labelFor(answer);
};

const formatNiceJobEstimate = ({
    config,
    fields,
    answers,
    resultText,
    result,
}) => {
    const lines = [
        'Estimate request',
        config.name || config.service || 'Job pricing calculator',
        '',
        `${config.content?.resultLabel || 'Estimated total'}: ${resultText}`,
    ];

    const pricedCategories = (result.categories || []).filter(
        (category) => category.subtotal > 0
    );
    if (pricedCategories.length) {
        lines.push('', 'Estimate breakdown:');
        pricedCategories.forEach((category) => {
            lines.push(
                `- ${category.name}: ${money(
                    category.subtotal,
                    config.currency
                )}`
            );
        });
    }

    if (fields.length) {
        lines.push('', 'Customer selections:');
        fields.forEach((field) => {
            lines.push(
                `- ${field.label}: ${formatAnswerForLeadForm(
                    field,
                    answers[field.id]
                )}`
            );
        });
    }

    return lines.join('\n');
};

const prefillNiceJobLongText = (value) => {
    if (typeof window === 'undefined' || typeof document === 'undefined')
        return;

    const fillField = () => {
        const forms = Array.from(
            document.querySelectorAll('.nj-modal-card--lead-form')
        );
        const openForm = forms.find(
            (form) =>
                !form.classList.contains('hide') && form.offsetParent !== null
        );
        const field = openForm?.querySelector('textarea.nj-text-field__area');

        if (!field) return false;
        if (field.value.trim()) return true;

        const setValue = Object.getOwnPropertyDescriptor(
            window.HTMLTextAreaElement.prototype,
            'value'
        )?.set;
        if (setValue) setValue.call(field, value);
        else field.value = value;
        field.dispatchEvent(new Event('input', { bubbles: true }));
        field.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
    };

    if (fillField()) return;
    const observer = new MutationObserver(() => {
        if (fillField()) observer.disconnect();
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
    });
    window.setTimeout(() => observer.disconnect(), 8000);
};

export default function JobPricingCalculator({
    CalculatorConfig = SAMPLE_CALCULATORS.windowCleaning,
    ColorScheme = 'dark-gradient',
    GradientDirection = 180,
    Alignment = 'left',
    VerticalAlignment = 'center',
    BackgroundImage = '',
    BackgroundImagePosition = 'center',
    ResultImage = '',
    ResultColorScheme = 'white',
    QuestionDisplay = 'all',
    VerticalSpacing = 'medium',
    Animation = 'fade',
    Duration = 0.5,
    AnimationGroup = 'content',
    StickyMedia = true,
    Anchor,
}) {
    const config = useMemo(
        () =>
            normalizeCalculatorConfig(
                CalculatorConfig?.fields
                    ? CalculatorConfig
                    : SAMPLE_CALCULATORS.windowCleaning
            ),
        [CalculatorConfig]
    );
    const [answers, setAnswers] = useState(() =>
        Object.fromEntries(
            (config.fields || [])
                .filter((field) => field.defaultValue !== undefined)
                .map((field) => [field.id, field.defaultValue])
        )
    );
    useEffect(() => {
        setAnswers(
            Object.fromEntries(
                (config.fields || [])
                    .filter((field) => field.defaultValue !== undefined)
                    .map((field) => [field.id, field.defaultValue])
            )
        );
    }, [config.id]);
    const result = useMemo(
        () => calculateEstimate(config, answers),
        [config, answers]
    );
    const fields = getVisibleFields(config, answers);
    const [activeQuestionId, setActiveQuestionId] = useState(null);
    const [questionDirection, setQuestionDirection] = useState(1);
    const questionDisplay = String(read(QuestionDisplay, 'all')).toLowerCase();
    const isQuestionStepper = questionDisplay === 'stepper';
    const activeQuestionIndex = Math.max(
        fields.findIndex((field) => field.id === activeQuestionId),
        0
    );
    const activeQuestion = fields[activeQuestionIndex];
    useEffect(() => {
        if (!fields.length) {
            setActiveQuestionId(null);
            return;
        }
        setActiveQuestionId((current) =>
            fields.some((field) => field.id === current)
                ? current
                : fields[0].id
        );
    }, [fields]);
    const colorScheme = read(ColorScheme, 'dark-gradient');
    const {
        backgroundColor,
        textColor,
        headerColor,
        taglineColor,
        backgroundStyle,
        overlayColor,
        overlayStyle,
    } = getColorScheme(colorScheme, read(GradientDirection, 180));
    const resultColorScheme = read(ResultColorScheme, 'white');
    const {
        backgroundColor: resultBackgroundColor,
        textColor: resultCardTextColor,
        taglineColor: resultTaglineColor,
        backgroundStyle: resultBackgroundStyle,
    } = getColorScheme(resultColorScheme, read(GradientDirection, 180));
    const resultUsesLightText = resultCardTextColor.includes('text-white');
    const resultMutedText = resultUsesLightText
        ? 'text-white/75'
        : 'text-neutral-600';
    const resultSubtleText = resultUsesLightText
        ? 'text-white/70'
        : 'text-neutral-500';
    const resultStrongText = resultUsesLightText
        ? 'text-white'
        : 'text-neutral-800';
    const resultDivider = resultUsesLightText
        ? 'border-white/20'
        : 'border-neutral-100';
    const content = config.content || {};
    const actions = config.actions || {};
    const primaryLink = String(read(actions.primaryLink, '') || '').trim();
    // `/contact` was this component's old generated default. Treat it as empty
    // so existing calculators receive the same NiceJob fallback as all other CTAs.
    const hasCustomPrimaryLink = Boolean(
        primaryLink && primaryLink !== '/contact'
    );
    const primaryOpenInNewTab = coerceBoolean(actions.primaryOpenInNewTab);
    const prefillNiceJobLead = coerceBoolean(
        actions.primaryPrefillNiceJobLead,
        false
    );
    const secondaryOpenInNewTab = coerceBoolean(actions.secondaryOpenInNewTab);
    const primaryIconVisible = coerceBoolean(actions.primaryIconVisible, true);
    const secondaryIconVisible = coerceBoolean(
        actions.secondaryIconVisible,
        true
    );
    const verticalSpacing = read(VerticalSpacing, 'medium');
    const spacing = getVerticalSpacing(verticalSpacing);
    const alignmentValue = String(read(Alignment, 'left')).toLowerCase();
    const alignment = alignmentValue === 'right' ? 'right' : 'left';
    const textAlignment = alignment === 'center' ? 'text-center' : 'text-left';
    const contentWidth = alignment === 'center' ? 'mx-auto' : '';
    const verticalAlignment = String(
        read(VerticalAlignment, 'center')
    ).toLowerCase();
    const gridAlignment =
        verticalAlignment === 'top'
            ? 'lg:items-start'
            : verticalAlignment === 'bottom'
            ? 'lg:items-end'
            : 'lg:items-center';
    const backgroundImage = String(read(BackgroundImage, '') || '').trim();
    const hasBackgroundImage = Boolean(backgroundImage);
    const resultImage = String(read(ResultImage, '') || '').trim();
    const animation = String(read(Animation, 'fade')).toLowerCase();
    const animationGroup = String(
        read(AnimationGroup, 'content')
    ).toLowerCase();
    const stickyResult = Boolean(read(StickyMedia, true));
    const centeredLayout = alignment === 'center';
    const rightAlignedLayout = alignment === 'right';
    const centeredResultLabelPosition = centeredLayout
        ? `lg:col-start-2 ${resultImage ? 'lg:row-start-2' : 'lg:row-start-1'}`
        : '';
    const centeredResultTotalPosition = centeredLayout
        ? `lg:col-start-2 ${resultImage ? 'lg:row-start-3' : 'lg:row-start-2'}`
        : '';
    const resultText =
        result.mode === 'custom-quote'
            ? 'This project requires a custom quote.'
            : result.mode === 'range'
            ? `${money(result.low, config.currency)}–${money(
                  result.high,
                  config.currency
              )}`
            : result.mode === 'starting'
            ? `Starting at ${money(result.total, config.currency)}`
            : money(result.total, config.currency);
    const moveToQuestion = (nextIndex) => {
        const nextField = fields[nextIndex];
        if (!nextField) return;
        setQuestionDirection(nextIndex > activeQuestionIndex ? 1 : -1);
        setActiveQuestionId(nextField.id);
    };
    const handlePrimaryLeadClick = () => {
        if (!prefillNiceJobLead || hasCustomPrimaryLink) return;
        prefillNiceJobLongText(
            formatNiceJobEstimate({
                config,
                fields,
                answers,
                resultText,
                result,
            })
        );
    };
    return (
        <section
            id={Anchor || 'job-pricing-calculator'}
            className={`relative ${backgroundColor} ${textColor} ${spacing}`}
            style={backgroundStyle}
            data-component="JobPricingCalculator"
        >
            {hasBackgroundImage && (
                <div className="absolute inset-0 z-0">
                    <div
                        className="absolute inset-0 bg-cover bg-no-repeat"
                        style={{
                            backgroundImage: `url(${backgroundImage})`,
                            backgroundPosition: read(
                                BackgroundImagePosition,
                                'center'
                            ),
                        }}
                    />
                    <div
                        className={`absolute inset-0 ${
                            overlayColor || 'bg-black/40'
                        }`}
                        style={overlayStyle}
                    />
                </div>
            )}
            <div
                className={`relative z-10 mx-auto grid max-w-6xl gap-10 px-5 lg:gap-16 lg:px-8 ${gridAlignment} ${
                    centeredLayout
                        ? 'lg:grid-cols-1'
                        : rightAlignedLayout
                        ? 'lg:grid-cols-[minmax(300px,.85fr)_minmax(0,1.15fr)]'
                        : 'lg:grid-cols-[minmax(0,1.15fr)_minmax(300px,.85fr)]'
                }`}
            >
                <AnimatedBlock
                    enabled={animationGroup === 'content'}
                    preset={animation}
                    duration={Duration}
                    className={`${textAlignment} ${contentWidth} ${
                        centeredLayout
                            ? 'lg:order-1'
                            : rightAlignedLayout
                            ? 'lg:order-2'
                            : 'lg:order-1'
                    }`}
                >
                    <p
                        className={`text-sm font-semibold uppercase tracking-[.18em] ${
                            taglineColor || 'text-primary-600'
                        }`}
                    >
                        {read(content.tagline, 'Instant estimate')}
                    </p>
                    <h2
                        className={`mt-3 text-3xl font-bold tracking-tight ${headerColor} md:text-4xl`}
                    >
                        {read(content.heading, 'Get your estimate')}
                    </h2>
                    <p className="mt-4 max-w-2xl text-base leading-7 opacity-80">
                        {read(content.description)}
                    </p>
                    <div className="mt-8">
                        {isQuestionStepper && fields.length > 1 && (
                            <p className="mb-4 text-xs font-semibold uppercase tracking-[.14em] opacity-70">
                                Question {activeQuestionIndex + 1} of{' '}
                                {fields.length}
                            </p>
                        )}
                        {isQuestionStepper ? (
                            <AnimatePresence initial={false} mode="wait">
                                {activeQuestion && (
                                    <motion.div
                                        key={activeQuestion.id}
                                        initial={{
                                            opacity: 0,
                                            x: questionDirection * 28,
                                        }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{
                                            opacity: 0,
                                            x: questionDirection * -28,
                                        }}
                                        transition={{ duration: 0.22 }}
                                    >
                                        <label className="mb-2 block text-sm font-semibold">
                                            {activeQuestion.label}
                                            {activeQuestion.required
                                                ? ' *'
                                                : ''}
                                        </label>
                                        {activeQuestion.helpText && (
                                            <p className="mb-2 text-xs opacity-70">
                                                {activeQuestion.helpText}
                                            </p>
                                        )}
                                        <Field
                                            field={activeQuestion}
                                            value={answers[activeQuestion.id]}
                                            textColor={textColor}
                                            onChange={(value) =>
                                                setAnswers((current) => ({
                                                    ...current,
                                                    [activeQuestion.id]: value,
                                                }))
                                            }
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        ) : (
                            <div className="grid gap-5">
                                {fields.map((field) => (
                                    <div key={field.id}>
                                        <label className="mb-2 block text-sm font-semibold">
                                            {field.label}
                                            {field.required ? ' *' : ''}
                                        </label>
                                        {field.helpText && (
                                            <p className="mb-2 text-xs opacity-70">
                                                {field.helpText}
                                            </p>
                                        )}
                                        <Field
                                            field={field}
                                            value={answers[field.id]}
                                            textColor={textColor}
                                            onChange={(value) =>
                                                setAnswers((current) => ({
                                                    ...current,
                                                    [field.id]: value,
                                                }))
                                            }
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                        {isQuestionStepper && fields.length > 1 && (
                            <div className="mt-6 flex items-center justify-start gap-2">
                                <button
                                    type="button"
                                    aria-label="Previous question"
                                    title="Previous question"
                                    onClick={() =>
                                        moveToQuestion(activeQuestionIndex - 1)
                                    }
                                    disabled={activeQuestionIndex === 0}
                                    className="border-current/20 inline-flex h-10 w-10 items-center justify-center rounded-full border transition hover:bg-white/10 disabled:pointer-events-none disabled:opacity-35"
                                >
                                    <ArrowLeftIcon
                                        className="h-4 w-4"
                                        aria-hidden="true"
                                    />
                                </button>
                                <button
                                    type="button"
                                    aria-label="Next question"
                                    title="Next question"
                                    onClick={() =>
                                        moveToQuestion(activeQuestionIndex + 1)
                                    }
                                    disabled={
                                        activeQuestionIndex ===
                                        fields.length - 1
                                    }
                                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-600 text-white transition hover:bg-primary-700 disabled:pointer-events-none disabled:opacity-35"
                                >
                                    <ArrowRightIcon
                                        className="h-4 w-4"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        )}
                    </div>
                </AnimatedBlock>
                <AnimatedBlock
                    enabled={animationGroup === 'results'}
                    preset={animation}
                    duration={Duration}
                    className={`${
                        stickyResult
                            ? 'lg:sticky lg:top-[calc(var(--nav-h,72px)+1rem)] lg:self-start'
                            : ''
                    } h-fit ${
                        resultImage && !stickyResult ? 'lg:self-stretch' : ''
                    } ${
                        centeredLayout
                            ? 'lg:order-2'
                            : rightAlignedLayout
                            ? 'lg:order-1'
                            : 'lg:order-2'
                    }`}
                >
                    <aside
                        className={`${
                            resultImage ? 'flex h-full flex-col' : ''
                        } rounded-[min(1.5rem,var(--shape-radius))] ${resultBackgroundColor} ${resultCardTextColor} p-6 shadow-xl ring-1 ring-black/5 ${
                            centeredLayout
                                ? 'lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-0'
                                : ''
                        }`}
                        style={resultBackgroundStyle}
                    >
                        {resultImage && (
                            <div
                                className={`mb-6 min-h-40 flex-1 overflow-hidden rounded-[min(1.5rem,var(--shape-radius))] bg-neutral-100 ${
                                    centeredLayout
                                        ? 'lg:col-start-2 lg:row-start-1 lg:mb-6 lg:h-52 lg:min-h-0 lg:flex-none'
                                        : ''
                                }`}
                            >
                                <img
                                    src={resultImage}
                                    alt=""
                                    className="h-full w-full object-cover"
                                />
                            </div>
                        )}
                        <p
                            className={`text-sm font-semibold uppercase tracking-[.16em] ${
                                resultTaglineColor || 'text-primary-600'
                            } ${centeredResultLabelPosition}`}
                        >
                            {read(content.resultLabel, 'Estimated total')}
                        </p>
                        <div
                            className={`mt-3 text-4xl font-bold tracking-tight md:text-5xl ${centeredResultTotalPosition}`}
                        >
                            {resultText}
                        </div>
                        {result.minimumApplied && (
                            <p
                                className={`mt-2 text-xs font-medium ${resultSubtleText} ${
                                    centeredLayout ? 'lg:col-start-2' : ''
                                }`}
                            >
                                Minimum service price applied.
                            </p>
                        )}
                        {result.mode !== 'custom-quote' &&
                            result.categories?.some(
                                (category) => category.subtotal > 0
                            ) && (
                                <dl
                                    className={`mt-5 space-y-2 border-t ${resultDivider} pt-4 text-sm ${
                                        centeredLayout
                                            ? 'lg:col-start-1 lg:row-span-6 lg:row-start-1 lg:self-center lg:border-r lg:border-t-0 lg:pr-8 lg:pt-0'
                                            : ''
                                    }`}
                                >
                                    {result.categories
                                        .filter(
                                            (category) => category.subtotal > 0
                                        )
                                        .map((category) => (
                                            <div
                                                key={category.id}
                                                className="flex items-center justify-between gap-3"
                                            >
                                                <dt className={resultMutedText}>
                                                    {category.name}
                                                </dt>
                                                <dd
                                                    className={`font-semibold ${resultStrongText}`}
                                                >
                                                    {money(
                                                        category.subtotal,
                                                        config.currency
                                                    )}
                                                </dd>
                                            </div>
                                        ))}
                                </dl>
                            )}
                        {result.mode === 'custom-quote' && (
                            <p
                                className={`mt-3 text-sm leading-6 ${resultMutedText} ${
                                    centeredLayout ? 'lg:col-start-2' : ''
                                }`}
                            >
                                {read(
                                    content.customQuoteMessage,
                                    'Call us to discuss your project.'
                                )}
                            </p>
                        )}
                        {content.disclaimer && (
                            <p
                                className={`mt-4 text-xs leading-5 ${resultSubtleText} ${
                                    centeredLayout ? 'lg:col-start-2' : ''
                                }`}
                            >
                                {content.disclaimer}
                            </p>
                        )}
                        <AnimatedBlock
                            enabled={animationGroup === 'ctas'}
                            preset={animation}
                            duration={Duration}
                            className={`mt-6 grid gap-3 ${
                                centeredLayout ? 'lg:col-start-2' : ''
                            }`}
                        >
                            {actions.primaryText &&
                                (hasCustomPrimaryLink ? (
                                    <Button
                                        asChild
                                        variant="primary"
                                        size="lg"
                                        iconDefault={read(
                                            actions.primaryIcon,
                                            ''
                                        )}
                                        iconVisible={primaryIconVisible}
                                    >
                                        <a
                                            href={primaryLink}
                                            {...(primaryOpenInNewTab
                                                ? {
                                                      target: '_blank',
                                                      rel: 'noreferrer',
                                                  }
                                                : {})}
                                        >
                                            {actions.primaryText}
                                        </a>
                                    </Button>
                                ) : (
                                    <Button
                                        variant="primary"
                                        size="lg"
                                        type="button"
                                        className="nj-lead"
                                        iconDefault={read(
                                            actions.primaryIcon,
                                            ''
                                        )}
                                        iconVisible={primaryIconVisible}
                                        onClick={handlePrimaryLeadClick}
                                    >
                                        {actions.primaryText}
                                    </Button>
                                ))}
                            {actions.secondaryText && (
                                <Button
                                    asChild
                                    variant="secondary"
                                    size="lg"
                                    iconDefault={read(
                                        actions.secondaryIcon,
                                        ''
                                    )}
                                    iconVisible={secondaryIconVisible}
                                >
                                    <a
                                        href={actions.secondaryLink || '#'}
                                        {...(secondaryOpenInNewTab
                                            ? {
                                                  target: '_blank',
                                                  rel: 'noreferrer',
                                              }
                                            : {})}
                                    >
                                        {actions.secondaryText}
                                    </a>
                                </Button>
                            )}
                        </AnimatedBlock>
                    </aside>
                </AnimatedBlock>
            </div>
        </section>
    );
}
